<template>
  <LegalPageLayout
    title="Terms of Service"
    :last-updated="lastUpdated"
    :show-table-of-contents="true"
  >
    <section id="acceptance-of-terms" role="region" aria-labelledby="acceptance-of-terms-heading">
      <h2 id="acceptance-of-terms-heading">1. Acceptance of Terms</h2>
      <p>
        By accessing and using WallpaperHub ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
      </p>
      <p>
        These Terms of Service ("Terms") constitute a legally binding agreement between you and WallpaperHub regarding your use of our wallpaper application and related services. Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms.
      </p>
    </section>

    <section id="description-of-service" role="region" aria-labelledby="description-of-service-heading">
      <h2 id="description-of-service-heading">2. Description of Service</h2>
      <p>
        WallpaperHub is a web-based application that provides users with access to a curated collection of high-quality wallpapers for personal and commercial use. Our service includes:
      </p>
      <ul>
        <li>Browse and search wallpapers by category, tags, and keywords</li>
        <li>Download wallpapers in various resolutions</li>
        <li>Create user accounts to save favorites and preferences</li>
        <li>Upload and share wallpapers (for authorized users)</li>
        <li>Administrative tools for content management</li>
      </ul>
      <p>
        We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
      </p>
    </section>

    <section id="user-accounts" role="region" aria-labelledby="user-accounts-heading">
      <h2 id="user-accounts-heading">3. User Accounts and Registration</h2>
      <h3 id="account-creation">3.1 Account Creation</h3>
      <p>
        To access certain features of the Service, you may be required to create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
      </p>
      
      <h3 id="account-security">3.2 Account Security</h3>
      <p>
        You are responsible for safeguarding the password and for maintaining the confidentiality of your account. You agree not to disclose your password to any third party and to take sole responsibility for any activities or actions under your account.
      </p>
      
      <h3 id="account-termination">3.3 Account Termination</h3>
      <p>
        We reserve the right to terminate or suspend your account at any time, with or without cause, and with or without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
      </p>
    </section>

    <section id="acceptable-use" role="region" aria-labelledby="acceptable-use-heading">
      <h2 id="acceptable-use-heading">4. Acceptable Use Policy</h2>
      <p>
        You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
      </p>
      <ul>
        <li>In any way that violates any applicable federal, state, local, or international law or regulation</li>
        <li>To transmit, or procure the sending of, any advertising or promotional material, including "junk mail," "chain letters," "spam," or any other similar solicitation</li>
        <li>To impersonate or attempt to impersonate the company, a company employee, another user, or any other person or entity</li>
        <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service</li>
        <li>To attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the Service</li>
        <li>To use any automated system, including "robots," "spiders," or "offline readers," to access the Service</li>
      </ul>
    </section>

    <section id="intellectual-property" role="region" aria-labelledby="intellectual-property-heading">
      <h2 id="intellectual-property-heading">5. Intellectual Property Rights</h2>
      <h3 id="our-content">5.1 Our Content</h3>
      <p>
        The Service and its original content, features, and functionality are and will remain the exclusive property of WallpaperHub and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
      </p>
      
      <h3 id="wallpaper-licenses">5.2 Wallpaper Licenses</h3>
      <p>
        Wallpapers available through our Service are provided under various licensing terms. Unless otherwise specified, wallpapers are available for personal use only. Commercial use may require additional licensing or permissions from the original creators or rights holders.
      </p>
      
      <h3 id="attribution">5.3 Attribution</h3>
      <p>
        Where required, proper attribution must be provided when using wallpapers from our Service. Attribution requirements, if any, will be clearly indicated with each wallpaper.
      </p>
    </section>

    <section id="user-generated-content" role="region" aria-labelledby="user-generated-content-heading">
      <h2 id="user-generated-content-heading">6. User-Generated Content</h2>
      <h3 id="content-submission">6.1 Content Submission</h3>
      <p>
        If you are authorized to upload content to the Service, you retain ownership of any intellectual property rights that you hold in that content. However, by submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and distribute your content in connection with the Service.
      </p>
      
      <h3 id="content-standards">6.2 Content Standards</h3>
      <p>
        All user-generated content must comply with our content standards. Content must not:
      </p>
      <ul>
        <li>Contain any material that is defamatory, obscene, indecent, abusive, offensive, harassing, violent, hateful, inflammatory, or otherwise objectionable</li>
        <li>Promote sexually explicit or pornographic material, violence, or discrimination</li>
        <li>Infringe any patent, trademark, trade secret, copyright, or other intellectual property rights</li>
        <li>Violate the legal rights of others or contain any material that could give rise to any civil or criminal liability</li>
        <li>Promote any illegal activity or advocate, promote, or assist any unlawful act</li>
      </ul>
      
      <h3 id="content-monitoring">6.3 Content Monitoring</h3>
      <p>
        We reserve the right, but not the obligation, to monitor, review, and remove user-generated content that violates these Terms or is otherwise objectionable, at our sole discretion and without notice.
      </p>
    </section>

    <section id="prohibited-activities" role="region" aria-labelledby="prohibited-activities-heading">
      <h2 id="prohibited-activities-heading">7. Prohibited Activities</h2>
      <p>
        You may not access or use the Service for any purpose other than that for which we make the Service available. The Service may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.
      </p>
      <p>
        Prohibited activities include, but are not limited to:
      </p>
      <ul>
        <li>Systematically retrieving data or other content from the Service to create or compile a collection, database, or directory</li>
        <li>Making any unauthorized use of the Service, including collecting usernames and/or email addresses of users</li>
        <li>Using the Service to advertise or offer to sell goods and services</li>
        <li>Circumventing, disabling, or otherwise interfering with security-related features of the Service</li>
        <li>Engaging in unauthorized framing of or linking to the Service</li>
        <li>Tricking, defrauding, or misleading us and other users</li>
        <li>Making improper use of our support services or submitting false reports of abuse or misconduct</li>
      </ul>
    </section>

    <section id="service-availability" role="region" aria-labelledby="service-availability-heading">
      <h2 id="service-availability-heading">8. Service Availability and Modifications</h2>
      <h3 id="availability">8.1 Service Availability</h3>
      <p>
        We strive to maintain the Service's availability, but we cannot guarantee uninterrupted access. The Service may be unavailable from time to time due to maintenance, updates, or technical issues beyond our control.
      </p>
      
      <h3 id="modifications">8.2 Service Modifications</h3>
      <p>
        We reserve the right to modify or discontinue, temporarily or permanently, the Service (or any part thereof) with or without notice. You agree that we shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the Service.
      </p>
      
      <h3 id="admin-responsibilities">8.3 Admin User Responsibilities</h3>
      <p>
        Users with administrative privileges have additional responsibilities:
      </p>
      <ul>
        <li>Maintain the confidentiality of administrative credentials</li>
        <li>Use administrative tools only for legitimate content management purposes</li>
        <li>Comply with all applicable laws and regulations when managing content</li>
        <li>Report any security vulnerabilities or unauthorized access immediately</li>
        <li>Ensure proper backup and data management procedures are followed</li>
      </ul>
    </section>

    <section id="disclaimers" role="region" aria-labelledby="disclaimers-heading">
      <h2 id="disclaimers-heading">9. Disclaimers and Limitations of Liability</h2>
      <h3 id="service-disclaimer">9.1 Service Disclaimer</h3>
      <p>
        The information on this Service is provided on an "as is" basis. To the fullest extent permitted by law, we exclude all representations, warranties, and conditions relating to our Service and the use of this Service.
      </p>
      
      <h3 id="wallpaper-disclaimer">9.2 Wallpaper Downloads</h3>
      <p>
        Wallpapers are provided for convenience and informational purposes only. We make no representations or warranties regarding the accuracy, completeness, or suitability of wallpapers for any particular purpose. Users download and use wallpapers at their own risk.
      </p>
      
      <h3 id="limitation-of-liability">9.3 Limitation of Liability</h3>
      <p>
        In no event shall WallpaperHub, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, punitive, consequential, or special damages arising out of or related to your use of the Service, even if we have been advised of the possibility of such damages.
      </p>
    </section>

    <section id="indemnification" role="region" aria-labelledby="indemnification-heading">
      <h2 id="indemnification-heading">10. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless WallpaperHub and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
      </p>
      <p>
        This indemnification obligation applies to claims arising from:
      </p>
      <ul>
        <li>Your use of and access to the Service</li>
        <li>Your violation of any term of these Terms</li>
        <li>Your violation of any third party right, including without limitation any copyright, property, or privacy right</li>
        <li>Any claim that your content caused damage to a third party</li>
      </ul>
    </section>

    <section id="termination" role="region" aria-labelledby="termination-heading">
      <h2 id="termination-heading">11. Termination</h2>
      <p>
        We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
      </p>
      <p>
        If you wish to terminate your account, you may simply discontinue using the Service. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
      </p>
    </section>

    <section id="governing-law" role="region" aria-labelledby="governing-law-heading">
      <h2 id="governing-law-heading">12. Governing Law and Dispute Resolution</h2>
      <h3 id="governing-law-clause">12.1 Governing Law</h3>
      <p>
        These Terms shall be interpreted and governed by the laws of the jurisdiction in which WallpaperHub operates, without regard to its conflict of law provisions.
      </p>
      
      <h3 id="dispute-resolution">12.2 Dispute Resolution</h3>
      <p>
        Any dispute arising from or relating to the subject matter of these Terms shall be finally settled by binding arbitration. The arbitration will be conducted in accordance with the rules of the applicable arbitration association.
      </p>
      
      <h3 id="class-action-waiver">12.3 Class Action Waiver</h3>
      <p>
        You agree that any arbitration or proceeding shall be limited to the dispute between us and you individually. You waive any right to participate in any class action or representative proceeding.
      </p>
    </section>

    <section id="changes-to-terms" role="region" aria-labelledby="changes-to-terms-heading">
      <h2 id="changes-to-terms-heading">13. Changes to Terms</h2>
      <p>
        We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
      </p>
      <p>
        What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms.
      </p>
      <p>
        It is your responsibility to review these Terms periodically for changes. Your continued use of the Service following the posting of any changes to these Terms constitutes acceptance of those changes.
      </p>
    </section>

    <section id="contact-information" role="region" aria-labelledby="contact-information-heading">
      <h2 id="contact-information-heading">14. Contact Information</h2>
      <p>
        If you have any questions about these Terms of Service, please contact us:
      </p>
      <ul>
        <li><strong>General Terms Questions:</strong> <a href="mailto:collinsodeny@gmail.com">collinsodeny@gmail.com</a></li>
        <li><strong>Business Inquiries:</strong> <a href="mailto:collinsodeny@gmail.com">collinsodeny@gmail.com</a></li>
        <li><strong>Technical Support:</strong> <a href="mailto:collinsodeny@gmail.com">collinsodeny@gmail.com</a></li>
      </ul>
      <p>
        We will make every effort to respond to your inquiries within 48 hours during business days.
      </p>
    </section>
  </LegalPageLayout>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import LegalPageLayout from '@/components/LegalPageLayout.vue'
import { seoManager } from '@/utils/seoUtils'

// Dynamic last updated date - in a real application, this could come from environment variables or a config file
const lastUpdated = '2024-01-15' // This should be updated whenever the terms are modified

onMounted(() => {
  // Set comprehensive SEO tags using the new SEO manager
  const metaTags = {
    title: 'Terms of Service - WallpaperHub',
    description: 'Read our Terms of Service to understand the rules and conditions for using WallpaperHub, including acceptable use, user responsibilities, intellectual property rights, and service limitations.',
    keywords: 'terms of service, user agreement, wallpaper app terms, acceptable use policy, user responsibilities, intellectual property, service conditions, user conduct, legal terms, service agreement',
    robots: 'index, follow',
    ogType: 'website'
  };
  
  seoManager.applyMetaTags(metaTags);
})
</script>